import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Search, CheckCircle, XCircle, Clock, ArrowUpDown, ArrowUp, ArrowDown, Flag, MapPin, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import {
  useAdminHostEvents,
  useReviewHostEvent,
  useRecomputeEventEmbeddings,
  type HostEventReviewStatus,
  type HostEventsFilters,
  type AdminHostEvent,
  type CityEventStats,
} from "../api/useAdminHostEvents";
import { useToast } from "@/components/ux/Toast";
import {
  useAdminEventReports,
  useUpdateReportStatus,
  type AdminEventReport,
  type ReportStatus,
} from "../api/useAdminEventReports";
import { AdminHostEventRow } from "../components/AdminHostEventRow";
import { Loader } from '@/components/Loader';

const REASON_LABELS: Record<string, string> = {
  spam:          "Spam or advertisement",
  inappropriate: "Inappropriate content",
  misleading:    "Misleading information",
  safety:        "Safety concern",
  other:         "Other",
};

function ReportStatusBadge({ status }: { status: ReportStatus }) {
  if (status === "reviewed")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-500">
        <CheckCircle className="w-3 h-3" /> Reviewed
      </span>
    );
  if (status === "dismissed")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-500/15 text-neutral-400">
        <XCircle className="w-3 h-3" /> Dismissed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400">
      <Clock className="w-3 h-3" /> Pending
    </span>
  );
}

function ReportRow({ report, onStatus }: { report: AdminEventReport; onStatus: (id: string, s: ReportStatus) => void }) {
  const reporterName = [report.reporterFirstName, report.reporterLastName].filter(Boolean).join(" ") || "Anonymous";
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="card space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">
              {REASON_LABELS[report.reason] ?? report.reason}
            </span>
            <ReportStatusBadge status={report.status} />
          </div>
          <p className="text-xs text-dark-muted mt-0.5">
            {report.eventName ?? "Unknown event"}
            {report.eventDate ? ` · ${report.eventDate}` : ""}
            {report.eventReviewStatus && report.eventReviewStatus !== "approved" && (
              <span className="ml-1 text-amber-400">({report.eventReviewStatus})</span>
            )}
          </p>
          <p className="text-xs text-dark-muted">
            By {reporterName}
            {report.reporterEmail ? ` (${report.reporterEmail})` : ""}
            {" · "}
            {new Date(report.createdAt).toLocaleDateString()}
          </p>
          {report.description && (
            <p className="text-sm mt-2 text-neutral-300 italic">"{report.description}"</p>
          )}
        </div>
        {report.eventSlug && (
          <a
            href={`/events/${report.eventSlug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-sm p-2 shrink-0"
            title="View event"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-dark-border">
        {report.status !== "reviewed" && (
          <button
            onClick={() => onStatus(report.id, "reviewed")}
            className="btn btn-sm bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border-0"
          >
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Mark reviewed
          </button>
        )}
        {report.status !== "dismissed" && (
          <button
            onClick={() => onStatus(report.id, "dismissed")}
            className="btn btn-sm bg-neutral-600/20 hover:bg-neutral-600/30 text-neutral-400 border-0"
          >
            <XCircle className="w-3.5 h-3.5 mr-1.5" /> Dismiss
          </button>
        )}
        {report.status !== "pending" && (
          <button
            onClick={() => onStatus(report.id, "pending")}
            className="btn btn-sm btn-ghost text-dark-muted"
          >
            <Clock className="w-3.5 h-3.5 mr-1.5" /> Reset
          </button>
        )}
      </div>
    </motion.div>
  );
}

const STATUS_TABS: { value: "all" | HostEventReviewStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const SORT_OPTIONS: { value: NonNullable<HostEventsFilters["sortBy"]>; label: string }[] = [
  { value: "updatedAt", label: "Last updated" },
  { value: "createdAt", label: "Date submitted" },
  { value: "date", label: "Event date" },
  { value: "name", label: "Name" },
];

const EventRow = AdminHostEventRow;

const REPORT_STATUS_TABS: { value: "all" | ReportStatus; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "reviewed", label: "Reviewed" },
  { value: "dismissed", label: "Dismissed" },
];

function ReportsPanel() {
  const [reportTab, setReportTab] = useState<"all" | ReportStatus>("pending");
  const { data, isLoading } = useAdminEventReports(reportTab);
  const updateStatus = useUpdateReportStatus();

  const reports = (data as any)?.data?.reports ?? [];
  const counts = (data as any)?.data?.counts ?? { pending: 0, reviewed: 0, dismissed: 0 };
  const totalAll = counts.pending + counts.reviewed + counts.dismissed;

  const handleStatus = useCallback(
    (id: string, status: ReportStatus) => updateStatus.mutate({ id, status }),
    [updateStatus],
  );

  return (
    <>
      <div className="flex gap-1 mb-6 bg-dark-card rounded-xl p-1 w-fit">
        {REPORT_STATUS_TABS.map((tab) => {
          const count =
            tab.value === "all" ? totalAll :
            tab.value === "pending" ? counts.pending :
            tab.value === "reviewed" ? counts.reviewed :
            counts.dismissed;
          return (
            <button
              key={tab.value}
              onClick={() => setReportTab(tab.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                reportTab === tab.value
                  ? "bg-primary-500 text-white"
                  : "text-dark-muted hover:text-white"
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  reportTab === tab.value ? "bg-white/20" : "bg-dark-border"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader />
        </div>
      ) : reports.length === 0 ? (
        <div className="card text-center py-16">
          <Flag className="w-12 h-12 text-dark-muted mx-auto mb-3" />
          <p className="text-lg font-semibold">
            {reportTab === "pending" ? "No pending reports" : "No reports found"}
          </p>
          <p className="text-dark-muted text-sm mt-1">
            {reportTab === "pending" ? "All caught up!" : "Try a different filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report: AdminEventReport) => (
            <ReportRow key={report.id} report={report} onStatus={handleStatus} />
          ))}
        </div>
      )}
    </>
  );
}

type Section = "review" | "reports";

export default function AdminHostEvents() {
  const [section, setSection] = useState<Section>("review");
  const [statusTab, setStatusTab] = useState<"all" | HostEventReviewStatus>("pending");
  const [search, setSearch] = useState("");
  const [cityId, setCityId] = useState<string>("all");
  const [sortBy, setSortBy] = useState<NonNullable<HostEventsFilters["sortBy"]>>("updatedAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const filters: HostEventsFilters = {
    reviewStatus: statusTab,
    search: search || undefined,
    cityId: cityId !== "all" ? cityId : undefined,
    sortBy,
    sortDir,
    limit: 100,
  };

  const { data, isLoading } = useAdminHostEvents(filters);
  const review = useReviewHostEvent();
  const recomputeEmbeddings = useRecomputeEventEmbeddings();
  const toast = useToast();

  const handleRecomputeEmbeddings = useCallback(() => {
    recomputeEmbeddings.mutate(
      { all: false },
      {
        onSuccess: (res) => {
          if (res.success && res.data) {
            const { total, succeeded, failed } = res.data;
            toast.success(
              total === 0
                ? "All events already have embeddings"
                : `Embeddings: ${succeeded} done${failed ? `, ${failed} failed` : ""} of ${total}`,
            );
          } else {
            toast.error(res.error || "Couldn't recompute embeddings");
          }
        },
        onError: (err) => toast.error((err as Error).message || "Couldn't recompute embeddings"),
      },
    );
  }, [recomputeEmbeddings, toast]);

  const events = (data as any)?.data?.events ?? [];
  const counts = (data as any)?.data?.counts ?? { pending: 0, approved: 0, rejected: 0 };
  const cityStats: CityEventStats[] = (data as any)?.data?.cityStats ?? [];
  const totalAll = counts.pending + counts.approved + counts.rejected;

  const handleReview = useCallback(
    (id: string, status: HostEventReviewStatus) => {
      review.mutate({ id, reviewStatus: status });
    },
    [review],
  );

  const toggleSort = (col: NonNullable<HostEventsFilters["sortBy"]>) => {
    if (sortBy === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const SortIcon = ({ col }: { col: NonNullable<HostEventsFilters["sortBy"]> }) => {
    if (sortBy !== col) return <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold mb-2">Host Event Review</h1>
            <p className="text-dark-muted">Review and approve community-hosted events before they appear publicly.</p>
          </div>
          <button
            type="button"
            onClick={handleRecomputeEmbeddings}
            disabled={recomputeEmbeddings.isPending}
            title="Compute multimodal embeddings for events that don't have one yet"
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-dark-card hover:bg-dark-border text-sm font-medium transition-colors disabled:opacity-50"
          >
            {recomputeEmbeddings.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 text-primary-400" />
            )}
            {recomputeEmbeddings.isPending ? "Computing…" : "Backfill embeddings"}
          </button>
        </div>
      </motion.div>

      {/* Top section switcher */}
      <div className="flex gap-1 mb-6 bg-dark-card rounded-xl p-1 w-fit">
        <button
          onClick={() => setSection("review")}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            section === "review" ? "bg-primary-500 text-white" : "text-dark-muted hover:text-white"
          }`}
        >
          Event Review
        </button>
        <button
          onClick={() => setSection("reports")}
          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
            section === "reports" ? "bg-primary-500 text-white" : "text-dark-muted hover:text-white"
          }`}
        >
          <Flag className="w-3.5 h-3.5" />
          Reports
        </button>
      </div>

      {section === "reports" ? (
        <ReportsPanel />
      ) : (
        <>
          {/* Status tabs */}
          <div className="flex gap-1 mb-4 bg-dark-card rounded-xl p-1 w-fit">
            {STATUS_TABS.map((tab) => {
              const tabCount =
                tab.value === "all" ? totalAll :
                tab.value === "pending" ? counts.pending :
                tab.value === "approved" ? counts.approved :
                counts.rejected;
              return (
                <button
                  key={tab.value}
                  onClick={() => setStatusTab(tab.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                    statusTab === tab.value
                      ? "bg-primary-500 text-white"
                      : "text-dark-muted hover:text-white"
                  }`}
                >
                  {tab.label}
                  {tabCount > 0 && (
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                      statusTab === tab.value ? "bg-white/20" : "bg-dark-border"
                    }`}>
                      {tabCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* City stats + filter */}
          {cityStats.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-1.5 mb-2">
                <MapPin className="w-3.5 h-3.5 text-dark-muted" />
                <span className="text-xs font-medium text-dark-muted uppercase tracking-wide">By City</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCityId("all")}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm border transition-colors ${
                    cityId === "all"
                      ? "bg-primary-500/20 border-primary-500/50 text-white"
                      : "border-dark-border text-dark-muted hover:text-white hover:border-neutral-500"
                  }`}
                >
                  <span className="font-medium">All Cities</span>
                </button>
                {cityStats.map((cs) => (
                  <button
                    key={cs.cityId}
                    onClick={() => setCityId(cs.cityId)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm border transition-colors ${
                      cityId === cs.cityId
                        ? "bg-primary-500/20 border-primary-500/50 text-white"
                        : "border-dark-border text-dark-muted hover:text-white hover:border-neutral-500"
                    }`}
                  >
                    <span className="font-medium">{cs.cityName}</span>
                    <span className="flex gap-1 text-xs">
                      {cs.pending > 0 && (
                        <span className="text-amber-400">{cs.pending}p</span>
                      )}
                      {cs.approved > 0 && (
                        <span className="text-emerald-400">{cs.approved}a</span>
                      )}
                      {cs.rejected > 0 && (
                        <span className="text-red-400">{cs.rejected}r</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search + sort controls */}
          <div className="flex gap-3 mb-6 flex-wrap items-center">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted pointer-events-none" />
              <input
                type="text"
                placeholder="Search by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-9 w-full"
              />
            </div>
            <div className="flex items-center gap-1 text-sm text-dark-muted">
              <span>Sort:</span>
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => toggleSort(opt.value)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg transition-colors ${
                    sortBy === opt.value
                      ? "bg-dark-card text-white"
                      : "hover:bg-dark-card text-dark-muted"
                  }`}
                >
                  {opt.label}
                  <SortIcon col={opt.value} />
                </button>
              ))}
            </div>
          </div>

          {/* Event list */}
          {isLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader />
            </div>
          ) : events.length === 0 ? (
            <div className="card text-center py-16">
              <CheckCircle className="w-12 h-12 text-dark-muted mx-auto mb-3" />
              <p className="text-lg font-semibold">
                {statusTab === "pending" ? "No events waiting for review" : "No events found"}
              </p>
              <p className="text-dark-muted text-sm mt-1">
                {statusTab === "pending" ? "All caught up!" : "Try adjusting your filters."}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((event: AdminHostEvent) => (
                <EventRow key={event.id} event={event} onReview={handleReview} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
